// CreateBranch.tsx
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredValidator,
  emailValidator,
  phoneValidator,
} from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createBranch,
  getAllBranches,
  getBranchByID,
  updateBranch,
} from "./services/branch.services";
import { clearBranchDetails } from "./branchSlice";
import { RxCross1 } from "react-icons/rx";
import { closed } from "../../components/drawer/drawerSlice";
import IconButton from "../../components/common/IconButton";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { findIndex } from "lodash";

interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");
  const cities = useAppSelector((state) => state.city?.CityList);

  useEffect(() => {
    const CityID = cityID ? cityID : undefined;
    const stateIndex = findIndex(cities, { ID: CityID });
    if (stateIndex > -1) {
      const city = cities[stateIndex];
      formRenderProps.onChange("StateID", { value: city?.StateID });
    } else {
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CityID", { value: "" });
    }
  }, [cityID]);
  return null;
};

const CreateBranch: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const BranchID = useAppSelector((state) => state.drawer.data);
  const BranchDetails = useAppSelector((state) => state.branch.BranchDetails);
  const loading = useAppSelector((state) => state.branch.formLoading);
  const cities = useAppSelector((state) => state.city?.CityList || []);
  const states = useAppSelector((state) => state.state?.StateList || []);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [BranchDetails]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());

    if (BranchID) {
      dispatch(getBranchByID(BranchID));
    }
    return () => {
      dispatch(clearBranchDetails());
    };
  }, [BranchID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      BranchName: values.BranchName ?? "",
      BranchAddress: values.BranchAddress ?? "",
      CityID: values.CityID ?? "",
      StateID: values.StateID ?? "",
      Pincode: values.Pincode ?? "",
      MobileNumber: values.MobileNumber ?? "",
      Email: values.Email ?? "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (BranchID) {
        const editPayload = {
          ...formData,
          ID: BranchID,
        };
        const response = await dispatch(updateBranch(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllBranches());
          dispatch(clearBranchDetails());
        }
      } else {
        const response = await dispatch(createBranch(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(getAllBranches());
          dispatch(clearBranchDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={BranchDetails}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <StateChangeWatcher formRenderProps={formRenderProps} />
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {BranchID ? "Update Branch" : "Add Branch"}
                  </Typography.h4>
                  <IconButton
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                    icon={RxCross1}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="BranchName"
                    label="Branch Name "
                    placeholder="Enter branch name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="BranchAddress"
                    label="Branch Address "
                    placeholder="Enter full branch address"
                    component={FormTextArea}
                    rows={3}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="CityID"
                    label="City "
                    placeholder="Select city"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={cities.map((city: any) => ({
                      value: city.ID,
                      label: city.CityName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="StateID"
                    label="State "
                    placeholder="Select state"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    disabled={true}
                    options={states.map((state: any) => ({
                      value: state.ID,
                      label: state.StateName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Pincode"
                    label="Pincode "
                    placeholder="Enter pincode"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="MobileNumber"
                    label="Mobile Number "
                    placeholder="10-digit mobile number"
                    component={FormTextField}
                    validator={phoneValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="Email"
                    label="Email "
                    placeholder="branch@example.com"
                    component={FormTextField}
                    validator={emailValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status?.value,
                      label: status?.label,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={BranchID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateBranch;
